import React from "react";
import { Badge } from "@/components/ui/badge";
import {
  BarChart3,
  TrendingUp,
  Users,
  Eye,
  Activity,
  MapPin,
  Smartphone,
  Search,
} from "lucide-react";
import DashboardWidget from "@/components/DashboardWidget";

interface AnalyticsStats {
  totalSessions: number;
  totalPageViews: number;
  uniqueVisitors: number;
  realTimeVisitors: number;
  trafficSources: {
    utm: Record<string, number>;
    searchEngines: Record<string, number>;
  };
  locationStats: Array<{ location: string; count: number }>;
  deviceStats: {
    types: Record<string, number>;
  };
  searchKeywords: Array<{ keyword: string; count: number }>;
}

interface DashboardMetricsWidgetProps {
  stats: AnalyticsStats | null;
}

export const DashboardMetricsWidget: React.FC<DashboardMetricsWidgetProps> = ({
  stats,
}) => {
  const totalUTMVisitors = Object.values(
    stats?.trafficSources.utm || {},
  ).reduce((a, b) => a + b, 0);
  const totalSearchVisitors = Object.values(
    stats?.trafficSources.searchEngines || {},
  ).reduce((a, b) => a + b, 0);
  const totalDevices = Object.values(stats?.deviceStats.types || {}).reduce(
    (a, b) => a + b,
    0,
  );

  const metrics = [
    {
      title: "Total Sessions",
      value: stats?.totalSessions || 0,
      icon: <Users className="h-5 w-5" />,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      format: "number",
    },
    {
      title: "Page Views",
      value: stats?.totalPageViews || 0,
      icon: <Eye className="h-5 w-5" />,
      color: "text-green-600",
      bgColor: "bg-green-50",
      format: "number",
    },
    {
      title: "Live Visitors",
      value: stats?.realTimeVisitors || 0,
      icon: <Activity className="h-5 w-5" />,
      color: "text-red-600",
      bgColor: "bg-red-50",
      format: "number",
      pulse: true,
    },
    {
      title: "Unique Visitors",
      value: stats?.uniqueVisitors || 0,
      icon: <TrendingUp className="h-5 w-5" />,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      format: "number",
    },
    {
      title: "UTM Sources",
      value: totalUTMVisitors,
      icon: <BarChart3 className="h-5 w-5" />,
      color: "text-indigo-600",
      bgColor: "bg-indigo-50",
      format: "number",
    },
    {
      title: "Search Traffic",
      value: totalSearchVisitors,
      icon: <Search className="h-5 w-5" />,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      format: "number",
    },
    {
      title: "Locations",
      value: stats?.locationStats?.length || 0,
      icon: <MapPin className="h-5 w-5" />,
      color: "text-teal-600",
      bgColor: "bg-teal-50",
      format: "number",
    },
    {
      title: "Devices",
      value: totalDevices,
      icon: <Smartphone className="h-5 w-5" />,
      color: "text-gray-600",
      bgColor: "bg-gray-50",
      format: "number",
    },
  ];

  const formatValue = (value: number, format: string) => {
    if (format === "number") {
      return value.toLocaleString();
    }
    return value.toString();
  };

  return (
    <DashboardWidget
      id="dashboard-metrics"
      title="Dashboard Overview"
      headerActions={<BarChart3 className="h-5 w-5" />}
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {metrics.map((metric, index) => (
          <div
            key={metric.title}
            className={`p-4 rounded-lg border ${metric.bgColor} hover:shadow-md transition-all duration-200`}
          >
            <div className="flex items-center justify-between mb-2">
              <div
                className={`${metric.color} ${metric.pulse ? "animate-pulse" : ""}`}
              >
                {metric.icon}
              </div>
              {index < 4 && (
                <Badge variant="outline" className="text-xs">
                  Primary
                </Badge>
              )}
            </div>
            <div className={`text-2xl font-bold ${metric.color} mb-1`}>
              {formatValue(metric.value, metric.format)}
            </div>
            <div className="text-sm text-gray-600 font-medium">
              {metric.title}
            </div>
          </div>
        ))}
      </div>

      {/* Quick insights */}
      <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-blue-600" />
          Quick Insights
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Avg. Pages per Session:</span>
            <span className="font-semibold">
              {stats?.totalSessions && stats?.totalPageViews
                ? (stats.totalPageViews / stats.totalSessions).toFixed(1)
                : "0"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Top Traffic Source:</span>
            <span className="font-semibold">
              {stats?.trafficSources.utm &&
              Object.keys(stats.trafficSources.utm).length > 0
                ? Object.entries(stats.trafficSources.utm)
                    .sort(([, a], [, b]) => b - a)[0]?.[0]
                    ?.replace("_", " ") || "N/A"
                : "N/A"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Most Popular Location:</span>
            <span className="font-semibold">
              {stats?.locationStats?.[0]?.location || "N/A"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Search Keywords:</span>
            <span className="font-semibold">
              {stats?.searchKeywords?.length || 0} tracked
            </span>
          </div>
        </div>
      </div>
    </DashboardWidget>
  );
};

export default DashboardMetricsWidget;
